// src/pages/SevenAmLibraryEp2.jsx
// 7AM Library — Episodio 2: El ruido invisible de las decisiones

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Headphones,
  BookOpen,
  Lightbulb,
  HelpCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import SevenAmSubscribe from '../components/SevenAmSubscribe';

// ─────────────────────────────────────────────────────────────────────────────
// REPRODUCTOR DE AUDIO
// ─────────────────────────────────────────────────────────────────────────────
function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };
  const handleTimeUpdate = () => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); };
  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleEnded = () => setPlaying(false);
  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      <div
        className="w-full h-1.5 bg-white/20 rounded-full mb-5 cursor-pointer relative overflow-hidden"
        onClick={handleSeek}
      >
        <div
          className="absolute inset-y-0 left-0 bg-amber-400 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-400 hover:bg-amber-300 flex items-center justify-center transition-colors shadow-lg"
          aria-label={playing ? 'Pausar' : 'Reproducir'}
        >
          {playing
            ? <Pause className="w-4 h-4 text-slate-900" strokeWidth={2.5} />
            : <Play className="w-4 h-4 text-slate-900 ml-0.5" strokeWidth={2.5} />
          }
        </button>
        <div className="flex items-center gap-1 text-xs font-mono tabular-nums text-white/60">
          <span className="text-white">{fmt(currentTime)}</span>
          <span>/</span>
          <span>{duration > 0 ? fmt(duration) : '~12:00'}</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={toggleMute}
          className="text-white/50 hover:text-white transition-colors"
          aria-label={muted ? 'Activar sonido' : 'Silenciar'}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SevenAmLibraryEp2() {
  return (
    <>
      <SEOHead
        title="Ep. 2 — El ruido invisible de las decisiones | 7AM Library"
        description="Por qué tus juicios son más inconsistentes de lo que crees — y qué le cuesta eso a tu organización. Basado en Noise, Daniel Kahneman, Olivier Sibony y Cass R. Sunstein."
      />

      <div className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
        <Navbar />

        {/* ── SECCIÓN 1+2+3: HEADER EDITORIAL ── */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              7AM Library
            </Link>

            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                7AM Library · Episodio 2 de 10
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-3">
                El ruido invisible de las decisiones
              </h1>
              <p className="text-base sm:text-xl text-slate-500 font-light tracking-wide leading-relaxed">
                Por qué tus juicios son más inconsistentes de lo que crees — y qué le
                cuesta eso a tu organización.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECCIÓN 5: AUDIO PLAYER ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-12">
          <div
            className="rounded-2xl overflow-hidden shadow-xl"
            style={{ background: 'linear-gradient(135deg, #0c1a2e 0%, #162944 60%, #0e2240 100%)' }}
          >
            <div className="h-1 w-full bg-gradient-to-r from-amber-400 to-yellow-500" />

            <div className="px-3 sm:px-10 py-5 sm:py-10">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Headphones className="w-3.5 h-3.5" />
                  Episodio 2 · ~12 minutos
                </div>
                <div className="inline-flex items-center gap-2 text-white/40 text-xs">
                  <BookOpen className="w-3.5 h-3.5" />
                  <em>Noise</em> · Kahneman, Sibony, Sunstein
                </div>
              </div>

              <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 italic max-w-2xl">
                &ldquo;Escucha cómo se analiza el error invisible que ninguna auditoría
                detecta.&rdquo;
              </p>

              <AudioPlayer src="/assets/library/Audios/ep-2.m4a" />
            </div>
          </div>
        </section>

        {/* ── CONTENIDO EDITORIAL ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="bg-slate-50 rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-8 py-6 sm:py-10 space-y-8 sm:space-y-10">

            {/* SECCIÓN 4 — REFERENCIA INTELECTUAL */}
            <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
              <BookOpen className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">
                  Conversación basada en
                </p>
                <p className="text-sm font-semibold text-slate-800 leading-snug">
                  Noise
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Daniel Kahneman, Olivier Sibony y Cass R. Sunstein
                </p>
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* SECCIÓN 6 — IMAGEN */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Imagen
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-1">
                El ruido, nadie lo ve
              </h2>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                Sesgo vs ruido — el error que ninguna auditoría detecta
              </p>

              <div className="flex justify-center">
                <img
                  src="/assets/library/Poster/ep-2.png"
                  alt="Infográfico de contraste en dos columnas. Columna izquierda azul marino: Sesgo — siempre en una dirección, se puede detectar, tiene corrección sistémica, comités dedicados a mitigarlo. Columna derecha teal: Ruido — sin dirección fija, invisible caso a caso, no hay mira que ajustar, nadie lo mide. Cápsula inferior: La trampa — el ruido solo aparece cuando comparas múltiples decisiones sobre casos similares. Frase de cierre: La consistencia de los juicios no es un detalle de calidad. Es la base de la fiabilidad del negocio. 7AM Library by EduTech Pulse Lab."
                  className="w-full md:max-w-xl rounded-xl border border-slate-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* SECCIÓN 7 — CONTEXTO */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Contexto
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-4">
                Contexto de esta conversación
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio es un destilado ejecutivo de{' '}
                <em>Noise</em> de Daniel Kahneman, Olivier Sibony y Cass R. Sunstein.
                No es un resumen del libro — es una conversación que extrae su idea
                más aplicable y la contrasta con situaciones reales de gestión y liderazgo.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio explora un error que las organizaciones acumulan sin saberlo:
                la variabilidad aleatoria en decisiones que deberían ser consistentes.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                No se trata de incompetencia individual. Se trata de un fallo estructural
                que los controles de calidad tradicionales no están diseñados para detectar.
              </p>
            </div>

            {/* SECCIÓN 8 — POR QUÉ IMPORTA */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 sm:p-6">
              <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2 tracking-[0.05em] uppercase">
                <Lightbulb className="w-4 h-4 text-amber-600" />
                Por qué importa
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Las organizaciones invierten tiempo y recursos en eliminar sesgos — errores
                sistemáticos en una dirección. Pero casi ninguna mide el ruido: la dispersión
                aleatoria entre decisiones equivalentes tomadas por personas distintas, o por
                la misma persona en momentos distintos.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                El costo no aparece en ningún reporte. Pero está ahí: en la prima de seguro
                que varía según quién la evalúe, en la contratación que depende del día de la
                entrevista, en el proyecto aprobado el martes y rechazado el viernes con la
                misma información. La viabilidad del negocio no debería depender del azar.
                Pero con frecuencia depende.
              </p>
            </div>

            {/* SECCIÓN 9 — IDEA CENTRAL */}
            <div className="border-l-4 border-amber-400 pl-5 py-1">
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Idea central
              </p>
              <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight leading-snug mb-4">
                El sesgo tiene dirección y se puede corregir. El ruido no tiene dirección
                — y por eso nadie lo ve.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Una organización ruidosa no tiene un problema de criterio. Tiene un problema
                de arquitectura.
              </p>
            </div>

            {/* SECCIÓN 10 — CORRECCIÓN EDITORIAL — solo metadata interna, no se muestra al lector */}

            <div className="border-t border-slate-200" />

            {/* SECCIÓN 11 — APLICACIÓN PRÁCTICA */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Aplicación práctica
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight mb-4">
                Aplicación práctica
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Piensa en una decisión que tú y al menos otro colega toman regularmente
                sobre casos similares: evaluar el desempeño de alguien, priorizar tareas,
                seleccionar entre propuestas o candidatos.
              </p>
              <div className="border-l-4 border-amber-400 pl-5 mb-4">
                <p className="text-base text-slate-700 leading-relaxed font-medium">
                  Esta semana, antes de comparar criterios con esa persona, escribe tu
                  evaluación por separado. Luego comparen.
                </p>
              </div>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Si llegan a conclusiones muy distintas sobre el mismo caso — eso es ruido.
                No es que uno tenga razón y el otro no. Es que el proceso no tiene suficiente
                estructura para producir consistencia.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed">
                <strong className="text-slate-600">Una pregunta concreta</strong> para tu
                próxima evaluación de persona o proyecto: ¿Cambiaría mi conclusión si lo
                revisara el viernes a las 6pm en lugar de hoy? Si la respuesta te incomoda,
                ya tienes el punto de partida.
              </p>
            </div>

            {/* SECCIÓN 12 — PREGUNTA DE REFLEXIÓN */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                Pregunta de reflexión
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <blockquote className="border-l-4 border-slate-300 pl-5 text-base sm:text-lg text-slate-700 leading-relaxed font-light italic">
                ¿Cuánta variabilidad aleatoria está tolerando tu organización en decisiones
                que considera estandarizadas — y qué mecanismos reales está construyendo
                para medirla?
              </blockquote>
            </div>

          </div>
        </section>

        <SevenAmSubscribe />

        {/* ── SECCIÓN 13: NAVEGACIÓN EPISODIOS ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Episodio anterior */}
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-6 py-5 sm:py-7">
              <p className="text-xs font-bold text-slate-400 tracking-[0.2em] uppercase mb-3">
                Episodio anterior
              </p>
              <div className="w-8 h-0.5 bg-slate-300 mb-4" />
              <p className="text-sm text-slate-600 font-semibold mb-1">
                Episodio 1 — El error que se repite no es mala suerte
              </p>
              <p className="text-sm text-slate-400 mb-4">Donella Meadows</p>
              <Link
                to="/7am-library/episodio-1"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Ir al episodio 1
              </Link>
            </div>

            {/* Episodio siguiente — próximamente */}
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-6 py-5 sm:py-7 opacity-60">
              <p className="text-xs font-bold text-slate-400 tracking-[0.2em] uppercase mb-3">
                Próximo episodio
              </p>
              <div className="w-8 h-0.5 bg-slate-200 mb-4" />
              <p className="text-sm text-slate-500 font-semibold mb-1">
                Episodio 3 — Thinking in Bets
              </p>
              <p className="text-sm text-slate-400 mb-4">Annie Duke</p>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                Ahora que sabes que tus decisiones son inconsistentes, ¿cómo evalúas si
                una decisión fue buena cuando el resultado pudo ser puro azar?
              </p>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-400 text-xs font-semibold rounded-lg cursor-not-allowed">
                Próximamente
              </span>
            </div>

          </div>
        </section>

        {/* ── SECCIÓN 14: PIE DE PÁGINA ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-12 sm:pb-16">
          <div className="border-t border-slate-200 pt-6 sm:pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-0.5">
                7AM Library by EduTech Pulse Lab
              </p>
              <p className="text-sm text-slate-500">
                No resumimos libros. Destilamos ideas para pensar, decidir y actuar mejor.
              </p>
            </div>
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Ver todos los episodios
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}
